import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Input} from 'antd'
import {observer} from 'mobx-react'
import {InputRef} from 'antd/lib/input'

import {MaskFieldProps} from './props'

interface MaskOptions {
    mask: string
    maskChar: string | null
    alwaysShowMask: boolean
    formatChars: {[key: string]: string}
    permanents: number[]
}

const defaultFormatChars = {
    '9': '[0-9]',
    a: '[A-Za-z]',
    '*': '[A-Za-z0-9]'
}

export const MaskField: React.FC<MaskFieldProps> = observer(
    ({
        mask = '',
        maskChar = '_',
        formatChars = defaultFormatChars,
        alwaysShowMask = false,
        beforeMaskedValueChange,
        value = '',
        onChange,
        inputRef,
        placeholder,
        disabled,
        className,
        ...otherProps
    }) => {
        const internalInputRef = useRef<InputRef>(null)
        const [internalValue, setInternalValue] = useState('')
        const [isFocused, setIsFocused] = useState(false)

        const parseMask = useCallback(
            (maskString: string): MaskOptions => {
                const permanents: number[] = []
                let parsedMask = ''
                let i = 0

                while (i < maskString.length) {
                    const char = maskString[i]

                    if (char === '\\' && i + 1 < maskString.length) {
                        const nextChar = maskString[i + 1]
                        parsedMask += nextChar
                        permanents.push(parsedMask.length - 1)
                        i += 2
                    } else if (formatChars[char]) {
                        parsedMask += char
                        i += 1
                    } else {
                        parsedMask += char
                        permanents.push(parsedMask.length - 1)
                        i += 1
                    }
                }

                return {
                    mask: parsedMask,
                    maskChar,
                    alwaysShowMask,
                    formatChars,
                    permanents
                }
            },
            [mask, maskChar, alwaysShowMask, formatChars]
        )

        const maskOptions = parseMask(mask)

        const applyMask = useCallback(
            (rawValue: string, maskOptions: MaskOptions): string => {
                if (!maskOptions.mask) return rawValue

                let maskedValue = ''
                let rawIndex = 0

                for (let maskIndex = 0; maskIndex < maskOptions.mask.length; maskIndex++) {
                    const maskChar = maskOptions.mask[maskIndex]
                    const isPermanent = maskOptions.permanents.includes(maskIndex)

                    if (isPermanent) {
                        maskedValue += maskChar
                    } else if (rawIndex < rawValue.length) {
                        const rawChar = rawValue[rawIndex]
                        const formatRegex = new RegExp(formatChars[maskChar] || '.*')

                        if (formatRegex.test(rawChar)) {
                            maskedValue += rawChar
                            rawIndex++
                        } else {
                            rawIndex++
                            maskIndex--
                        }
                    } else {
                        if (maskOptions.maskChar !== null && maskOptions.maskChar !== '') {
                            maskedValue += maskOptions.maskChar
                        }
                    }
                }

                return maskedValue
            },
            [formatChars]
        )

        const removeMask = useCallback((maskedValue: string, maskOptions: MaskOptions): string => {
            if (!maskOptions.mask) return maskedValue

            let rawValue = ''

            for (let i = 0; i < maskedValue.length && i < maskOptions.mask.length; i++) {
                const isPermanent = maskOptions.permanents.includes(i)

                if (!isPermanent && maskedValue[i] !== maskOptions.maskChar) {
                    rawValue += maskedValue[i]
                }
            }

            return rawValue
        }, [])

        const getRawCursorPosition = useCallback(
            (maskedPosition: number, maskOptions: MaskOptions): number => {
                let rawPosition = 0

                for (let i = 0; i < maskedPosition && i < maskOptions.mask.length; i++) {
                    if (!maskOptions.permanents.includes(i)) {
                        rawPosition++
                    }
                }

                return rawPosition
            },
            []
        )

        const getMaskedCursorPosition = useCallback(
            (rawPosition: number, maskOptions: MaskOptions): number => {
                let maskedPosition = 0
                let rawCount = 0

                for (let i = 0; i < maskOptions.mask.length; i++) {
                    if (!maskOptions.permanents.includes(i)) {
                        if (rawCount >= rawPosition) {
                            break
                        }
                        rawCount++
                    }
                    maskedPosition++
                }

                return maskedPosition
            },
            []
        )

        const updateValue = useCallback(
            (newRawValue: string, cursorPosition?: number) => {
                const maskedValue = applyMask(newRawValue, maskOptions)
                const shouldShowMask = alwaysShowMask || isFocused || maskedValue.length > 0

                let finalValue = shouldShowMask ? maskedValue : ''
                let finalSelection: {start: number; end: number} | null = null

                if (cursorPosition !== undefined) {
                    const maskedCursorPos = getMaskedCursorPosition(cursorPosition, maskOptions)
                    finalSelection = {start: maskedCursorPos, end: maskedCursorPos}
                }

                if (beforeMaskedValueChange) {
                    const oldState = {
                        value: internalValue,
                        selection: null
                    }

                    const newState = {
                        value: finalValue,
                        selection: finalSelection
                    }

                    const result = beforeMaskedValueChange(newState, oldState, null, maskOptions)
                    finalValue = result.value
                    finalSelection = result.selection
                }

                setInternalValue(finalValue)

                if (finalSelection && internalInputRef.current?.input) {
                    // Use requestAnimationFrame instead of setTimeout for smoother cursor updates
                    requestAnimationFrame(() => {
                        if (internalInputRef.current?.input) {
                            internalInputRef.current.input.setSelectionRange(
                                finalSelection!.start,
                                finalSelection!.end
                            )
                        }
                    })
                }
            },
            [
                applyMask,
                maskOptions,
                alwaysShowMask,
                isFocused,
                beforeMaskedValueChange,
                internalValue,
                getMaskedCursorPosition
            ]
        )

        useEffect(() => {
            if (value !== undefined) {
                const rawValue = removeMask(value, maskOptions)
                updateValue(rawValue)
            }
        }, [value, removeMask, maskOptions, updateValue])

        useEffect(() => {
            if (value === undefined && !internalValue) {
                updateValue('')
            }
        }, [value, internalValue, updateValue])

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const input = e.target as HTMLInputElement
            const maskedValue = input.value
            const cursorPosition = input.selectionStart || 0

            // Extract raw value from the new masked value
            const rawValue = removeMask(maskedValue, maskOptions)

            // Calculate where the cursor should be in the raw value
            let rawCursorPosition = 0
            let maskedIndex = 0

            // Count non-permanent characters up to cursor position
            while (maskedIndex < cursorPosition && maskedIndex < maskedValue.length) {
                if (!maskOptions.permanents.includes(maskedIndex)) {
                    rawCursorPosition++
                }
                maskedIndex++
            }

            // Update the value without async cursor positioning
            setInternalValue(applyMask(rawValue, maskOptions))

            // Set cursor position synchronously
            if (internalInputRef.current?.input) {
                const newMaskedCursorPos = getMaskedCursorPosition(rawCursorPosition, maskOptions)
                requestAnimationFrame(() => {
                    if (internalInputRef.current?.input) {
                        internalInputRef.current.input.setSelectionRange(
                            newMaskedCursorPos,
                            newMaskedCursorPos
                        )
                    }
                })
            }

            if (onChange) {
                const newMaskedValue = applyMask(rawValue, maskOptions)
                const syntheticEvent = {
                    ...e,
                    target: {
                        ...e.target,
                        value: newMaskedValue
                    }
                }
                onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>)
            }
        }

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(true)

            if (alwaysShowMask || !internalValue) {
                const rawValue = removeMask(internalValue, maskOptions)
                updateValue(rawValue)
            }

            if (otherProps.onFocus) {
                otherProps.onFocus(e)
            }
        }

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(false)

            if (!alwaysShowMask && !removeMask(internalValue, maskOptions)) {
                setInternalValue('')
            }

            if (otherProps.onBlur) {
                otherProps.onBlur(e)
            }
        }

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            const input = e.target as HTMLInputElement
            const cursorPosition = input.selectionStart || 0
            const selectionEnd = input.selectionEnd || 0

            if (e.key === 'Backspace' && !e.metaKey && !e.ctrlKey && !e.altKey) {
                e.preventDefault()

                const currentRawValue = removeMask(internalValue, maskOptions)
                let newRawValue = currentRawValue
                let newCursorPosition = cursorPosition

                if (cursorPosition !== selectionEnd) {
                    // Selection exists - delete selected content
                    const rawStart = getRawCursorPosition(cursorPosition, maskOptions)
                    const rawEnd = getRawCursorPosition(selectionEnd, maskOptions)

                    newRawValue =
                        currentRawValue.substring(0, rawStart) + currentRawValue.substring(rawEnd)
                    newCursorPosition = rawStart
                } else if (cursorPosition > 0) {
                    // No selection - delete one character before cursor
                    let targetPosition = cursorPosition - 1

                    // Skip permanent characters when moving backward
                    while (targetPosition >= 0 && maskOptions.permanents.includes(targetPosition)) {
                        targetPosition--
                    }

                    if (targetPosition >= 0) {
                        const rawPosition = getRawCursorPosition(targetPosition, maskOptions)
                        if (rawPosition < currentRawValue.length) {
                            newRawValue =
                                currentRawValue.substring(0, rawPosition) +
                                currentRawValue.substring(rawPosition + 1)
                            newCursorPosition = rawPosition
                        }
                    }
                }

                updateValue(newRawValue, newCursorPosition)

                if (onChange) {
                    const newMaskedValue = applyMask(newRawValue, maskOptions)
                    const syntheticEvent = {
                        target: {value: newMaskedValue}
                    } as React.ChangeEvent<HTMLInputElement>
                    onChange(syntheticEvent)
                }

                return
            }

            if (e.key === 'Delete' && !e.metaKey && !e.ctrlKey && !e.altKey) {
                e.preventDefault()

                const currentRawValue = removeMask(internalValue, maskOptions)
                let newRawValue = currentRawValue
                let newCursorPosition = cursorPosition

                if (cursorPosition !== selectionEnd) {
                    // Selection exists - delete selected content
                    const rawStart = getRawCursorPosition(cursorPosition, maskOptions)
                    const rawEnd = getRawCursorPosition(selectionEnd, maskOptions)

                    newRawValue =
                        currentRawValue.substring(0, rawStart) + currentRawValue.substring(rawEnd)
                    newCursorPosition = rawStart
                } else if (cursorPosition < internalValue.length) {
                    // No selection - delete one character after cursor
                    let targetPosition = cursorPosition

                    // Skip permanent characters when moving forward
                    while (
                        targetPosition < maskOptions.mask.length &&
                        maskOptions.permanents.includes(targetPosition)
                    ) {
                        targetPosition++
                    }

                    if (targetPosition < maskOptions.mask.length) {
                        const rawPosition = getRawCursorPosition(targetPosition, maskOptions)
                        if (rawPosition < currentRawValue.length) {
                            newRawValue =
                                currentRawValue.substring(0, rawPosition) +
                                currentRawValue.substring(rawPosition + 1)
                            newCursorPosition = getRawCursorPosition(cursorPosition, maskOptions)
                        }
                    }
                }

                updateValue(newRawValue, newCursorPosition)

                if (onChange) {
                    const newMaskedValue = applyMask(newRawValue, maskOptions)
                    const syntheticEvent = {
                        target: {value: newMaskedValue}
                    } as React.ChangeEvent<HTMLInputElement>
                    onChange(syntheticEvent)
                }

                return
            }

            // Call original onKeyDown if provided
            if (otherProps.onKeyDown) {
                otherProps.onKeyDown(e)
            }
        }

        const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
            e.preventDefault()

            const pastedText = e.clipboardData.getData('text')
            const input = e.target as HTMLInputElement
            const start = input.selectionStart || 0
            const end = input.selectionEnd || 0

            const currentRawValue = removeMask(internalValue, maskOptions)
            const rawStart = getRawCursorPosition(start, maskOptions)
            const rawEnd = getRawCursorPosition(end, maskOptions)

            const newRawValue =
                currentRawValue.substring(0, rawStart) +
                pastedText +
                currentRawValue.substring(rawEnd)

            updateValue(newRawValue, rawStart + pastedText.length)

            if (onChange) {
                const newMaskedValue = applyMask(newRawValue, maskOptions)
                const syntheticEvent = {
                    target: {value: newMaskedValue}
                } as React.ChangeEvent<HTMLInputElement>
                onChange(syntheticEvent)
            }
        }

        const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
            // Always move cursor to the start (first non-permanent character position)
            const input = e.target as HTMLInputElement
            
            // Find the first non-permanent character position
            let startPosition = 0
            for (let i = 0; i < maskOptions.mask.length; i++) {
                if (!maskOptions.permanents.includes(i)) {
                    startPosition = i
                    break
                }
            }

            // Set cursor to start position
            requestAnimationFrame(() => {
                if (internalInputRef.current?.input) {
                    internalInputRef.current.input.setSelectionRange(startPosition, startPosition)
                }
            })

            // Call original onClick if provided
            if (otherProps.onClick) {
                otherProps.onClick(e)
            }
        }

        useEffect(() => {
            if (inputRef && internalInputRef.current?.input) {
                inputRef(internalInputRef.current.input)
            }
        }, [inputRef])

        return (
            <Input
                {...otherProps}
                ref={internalInputRef}
                value={internalValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onPaste={handlePaste}
                onClick={handleClick}
                placeholder={placeholder}
                disabled={disabled}
                className={className}
            />
        )
    }
)
